"use client";

import { ArrowRight, Clock, MapPin } from "@phosphor-icons/react";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";
import type { Locale } from "@/i18n/config";

type GatheringsTranslations = {
  sectionLabel: string;
  title: string;
  titleHighlight: string;
  description: string;
  joinService: string;
  youthAflame: string;
  aflame: string;
  serviceDescription: string;
  whenLabel: string;
  whenValue: string;
  whereLabel: string;
  whereValue: string;
  watchOnline: string;
  registerSummit: string;
};

type GatheringsSectionProps = {
  locale?: Locale;
  translations?: GatheringsTranslations;
};

export const GatheringsSection = ({ locale = 'en', translations }: GatheringsSectionProps) => {
  // Default translations for backwards compatibility
  const t: GatheringsTranslations = translations || {
    sectionLabel: "The Experience",
    title: "Weekly",
    titleHighlight: "Encounters",
    description: "Sunday is just the beginning. Discover how we gather throughout the week across Canada.",
    joinService: "Join the Service",
    youthAflame: "Youth",
    aflame: "Aflame",
    serviceDescription: "Experience spirit-led worship, powerful Word, and divine encounters that set your soul on fire.",
    whenLabel: "When",
    whenValue: "Sundays, 12:00 PM",
    whereLabel: "Where",
    whereValue: "Across Canada",
    watchOnline: "Watch Online",
    registerSummit: "Register for Revival Summit",
  };
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  // Re-implemented the bold expansion effect
  const scale = useTransform(scrollYProgress, [0, 0.4], [0.8, 1]);
  const rotateX = useTransform(scrollYProgress, [0, 0.4], [15, 0]);
  const y = useTransform(scrollYProgress, [0, 0.4], [100, 0]);
  const borderRadius = useTransform(scrollYProgress, [0, 0.4], ["8rem", "3.5rem"]);

  return (
    <section
      id="gatherings"
      ref={containerRef}
      className="py-32 bg-white dark:bg-black px-6 transition-colors relative overflow-hidden [perspective:1000px]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header remains outside the expanding card */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-12 mb-24 relative z-20">
          <div className="max-w-3xl">
            <span className="text-brand-red text-sm tracking-[0.4em] uppercase mb-6 block font-black">
              {t.sectionLabel}
            </span>
            <h2 className="font-serif text-6xl lg:text-8xl font-bold leading-[0.85] text-black dark:text-white tracking-tighter">
              {t.title} <br />
              <span className="text-brand-gold-text dark:text-brand-gold italic">{t.titleHighlight}</span>
            </h2>
          </div>
          <p className="text-xl text-zinc-500 dark:text-zinc-400 max-w-md lg:text-right font-light leading-relaxed">
            {t.description}
          </p>
        </div>

        <motion.div
          style={{
            scale,
            rotateX,
            y,
            borderRadius,
          }}
          className="relative overflow-hidden group shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] dark:shadow-[0_50px_100px_-20px_rgba(255,255,255,0.05)] min-h-[700px] flex items-center bg-zinc-900"
        >
          <div className="absolute inset-0">
            <Image
              src="/ayac/_DSC2160.JPG"
              alt="Youth Aflame"
              fill
              className="object-cover transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/40 to-transparent" />
          </div>

          <div className="relative z-10 p-12 lg:p-24 lg:max-w-3xl text-white md:pt-24">
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden sm:block text-brand-gold text-xs tracking-[0.5em] uppercase mb-8 font-black"
            >
              {t.joinService}
            </motion.span>

            <h3 className="font-serif text-5xl lg:text-7xl font-bold mb-8 italic tracking-tight leading-none -mt-6 sm:mt-0">
              {t.youthAflame} <span className="text-brand-red">{t.aflame}</span>
            </h3>

            <p className="text-xl lg:text-2xl text-white/80 mb-16 leading-relaxed font-light max-w-2xl">
              {t.serviceDescription}
            </p>

            <div className="flex flex-wrap items-center gap-10 mb-16">
              <div className="flex items-center gap-5 group/item">
                <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20 group-hover/item:bg-brand-red transition-all duration-500">
                  <Clock weight="fill" className="w-7 h-7 text-brand-gold group-hover/item:text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">{t.whenLabel}</span>
                  <span className="text-white font-bold tracking-tight text-xl">{t.whenValue}</span>
                </div>
              </div>

              <div className="flex items-center gap-5 group/item">
                <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20 group-hover/item:bg-brand-red transition-all duration-500">
                  <MapPin weight="fill" className="w-7 h-7 text-brand-gold group-hover/item:text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">{t.whereLabel}</span>
                  <span className="text-white font-bold tracking-tight text-xl">{t.whereValue}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                href={`/${locale}/watch`}
                className="inline-flex items-center gap-4 bg-white text-black px-10 py-5 rounded-full font-bold uppercase text-[10px] tracking-widest hover:bg-brand-red hover:text-white transition-all shadow-xl"
              >
                {t.watchOnline}
                <ArrowRight weight="bold" />
              </Link>
              <Link
                href={`/${locale}/yafrs`}
                className="inline-flex items-center gap-4 bg-brand-red text-white px-10 py-5 rounded-full font-bold uppercase text-[10px] tracking-widest hover:bg-brand-red/80 transition-all shadow-xl shadow-brand-red/20"
              >
                {t.registerSummit}
                <ArrowRight weight="bold" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
